// @spec INC-STR-002
'use client';

import { useAuth, useUser } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { AlertCircle } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card, CardContent } from '@/components/ui/card';
import { validateQuickReportShareLink } from '@/features/incidents/api/quick-reports';
import { QuickReportForm } from '@/features/incidents/components/QuickReportForm';
import i18n from '@/lib/i18n';

export const Route = createFileRoute('/quick-report/$shareToken')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.quickReport'),
  }),
  component: PublicQuickReportPage,
});

function PublicQuickReportPage() {
  const { shareToken } = Route.useParams();
  const { isSignedIn } = useUser();
  const { getToken } = useAuth();

  const { isLoading, isError } = useQuery({
    queryKey: ['quick-reports', 'share-link', shareToken],
    queryFn: () => validateQuickReportShareLink(shareToken, isSignedIn ? getToken : undefined),
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-3xl">
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground">Loading quick report form...</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-3xl">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Invalid link</AlertTitle>
            <AlertDescription>
              This quick report link is invalid or has expired.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mx-auto max-w-3xl space-y-6">
        <header>
          <h1 className="text-3xl font-semibold text-foreground">Quick Report</h1>
          <p className="mt-1 text-muted-foreground">
            Share what happened. Keep it brief and submit.
          </p>
        </header>
        <QuickReportForm shareToken={shareToken} />
      </div>
    </div>
  );
}
